import { SearchBox } from "../../../../components/Search";
import { JvvnlPlantType } from "../../../../models/jvvnl";
import {
  isolateOnAllSelected,
  pickLastSingle,
  pickSingleWithIsolate,
} from "./constants";

type Opt<V = number> = { value: V; label: string };

export type JvvnlEntitySearchBarProps = {
  show: {
    circle: boolean;
    feeder: boolean;
    substation: boolean;
    discomSubstation: boolean;
    netConsumption: boolean;
    generation: boolean;
  };
  // Which boxes of the Net Consumption block to render. Omit = all three
  // (FeederDemand). Popups pass level-based visibility (e.g. circle → circle only).
  netLevels?: { circle: boolean; transco: boolean; discom: boolean };
  hierSepClass: string;

  // Circle (shared)
  circleOptions: Opt[];
  selectedCircle: Opt | null;
  setSelectedCircle: (o: Opt | null) => void;

  // Feeder block
  selectedFeederTransco: Opt | null;
  setSelectedFeederTransco: (o: Opt | null) => void;
  setSelectedFeederSubstations: (o: Opt<string>[]) => void;
  selectedFeederSubstations: Opt<string>[];
  feederSubstationOptions: Opt<string>[];
  transcoOptionsForDiscom: Opt[];
  filteredFeederOptions: Opt[];
  selectedFeederOptions: Opt[];
  setSelectedFeederOptions: (o: Opt[]) => void;
  feederOpenSignal: number;

  // Substation block
  substationOptions: Opt[];
  selectedSubstationOptions: Opt[];
  setSelectedSubstationOptions: (o: Opt[]) => void;

  // Discom-substation block
  selectedTranscoForDiscom: Opt[];
  setSelectedTranscoForDiscom: (o: Opt[]) => void;
  filteredDiscomSubstationOptions: Opt[];
  selectedDiscomSubstations: Opt[];
  setSelectedDiscomSubstations: (o: Opt[]) => void;

  // Net-consumption block
  netCircleOptions: Opt<string>[];
  netCircle: Opt<string> | null;
  setNetCircle: (o: Opt<string> | null) => void;
  netTranscoOptions: Opt[];
  netTransco: Opt | null;
  setNetTransco: (o: Opt | null) => void;
  netDiscomOptions: Opt[];
  netDiscom: Opt | null;
  setNetDiscom: (o: Opt | null) => void;
  discomSubstations: {
    discom_substation_id: number;
    transco_substation_id?: number | null;
  }[];

  // Generation block
  genCircleOptions: Opt[];
  selectedGenCircle: Opt | null;
  setSelectedGenCircle: (o: Opt | null) => void;
  plantTypeOptions: Opt<JvvnlPlantType>[];
  selectedPlantTypeOptions: Opt<JvvnlPlantType>[];
  setSelectedPlantTypeOptions: (o: Opt<JvvnlPlantType>[]) => void;
  plantOptions: Opt[];
  selectedPlantOptions: Opt[];
  setSelectedPlantOptions: (o: Opt[]) => void;
  allPlants: {
    id: number;
    plant_type?: JvvnlPlantType;
    circle_id?: number | null;
  }[];
};

export function JvvnlEntitySearchBar(p: JvvnlEntitySearchBarProps) {
  const {
    show,
    netLevels = { circle: true, transco: true, discom: true },
    hierSepClass,
    circleOptions,
    selectedCircle,
    setSelectedCircle,
    selectedFeederTransco,
    setSelectedFeederTransco,
    setSelectedFeederSubstations,
    selectedFeederSubstations,
    feederSubstationOptions,
    transcoOptionsForDiscom,
    filteredFeederOptions,
    selectedFeederOptions,
    setSelectedFeederOptions,
    feederOpenSignal,
    substationOptions,
    selectedSubstationOptions,
    setSelectedSubstationOptions,
    selectedTranscoForDiscom,
    setSelectedTranscoForDiscom,
    filteredDiscomSubstationOptions,
    selectedDiscomSubstations,
    setSelectedDiscomSubstations,
    netCircleOptions,
    netCircle,
    setNetCircle,
    netTranscoOptions,
    netTransco,
    setNetTransco,
    netDiscomOptions,
    netDiscom,
    setNetDiscom,
    discomSubstations,
    genCircleOptions,
    selectedGenCircle,
    setSelectedGenCircle,
    plantTypeOptions,
    selectedPlantTypeOptions,
    setSelectedPlantTypeOptions,
    plantOptions,
    selectedPlantOptions,
    setSelectedPlantOptions,
    allPlants,
  } = p;

  return (
    <div className="flex flex-wrap gap-x-3 gap-y-2 items-center min-w-0">
      {show.circle && (
        <SearchBox
          autoWidth
          sortAlphabetically={true}
          label="CIRCLE"
          options={circleOptions}
          placeholder="Circle"
          minWidth={140}
          isClearable
          selectedOptions={selectedCircle ? [selectedCircle] : []}
          onChange={(v: any) => setSelectedCircle(pickLastSingle(v))}
        />
      )}
      {show.feeder && (
        <>
          <SearchBox
            sortAlphabetically={true}
            label="CIRCLE"
            options={circleOptions}
            placeholder="Circle"
            minWidth={140}
            isClearable
            selectedOptions={selectedCircle ? [selectedCircle] : []}
            onChange={(v: any) => {
              setSelectedCircle(pickLastSingle(v));
              setSelectedFeederTransco(null);
              setSelectedFeederSubstations([]);
              setSelectedFeederOptions([]);
            }}
          />
          <span className={hierSepClass}>›</span>
          <SearchBox
            sortAlphabetically={true}
            autoWidth
            label="TRANSCO S/S"
            options={transcoOptionsForDiscom}
            placeholder="Transco Substation"
            minWidth={140}
            maxWidth={260}
            isClearable
            selectedOptions={
              selectedFeederTransco ? [selectedFeederTransco] : []
            }
            onChange={(v: any) => {
              const next = pickLastSingle(v);
              setSelectedFeederTransco(next);
              // Clear descendants so backfill can't refill them
              // (and so a cleared transco truly resets the chain).
              setSelectedFeederSubstations([]);
              setSelectedFeederOptions([]);
            }}
          />
          <span className={hierSepClass}>›</span>
          <SearchBox
            isMany
            autoWidth
            isClearable
            label="DISCOM S/S"
            sortAlphabetically={true}
            options={feederSubstationOptions}
            placeholder="Discom Substation"
            minWidth={140}
            maxWidth={260}
            selectedOptions={selectedFeederSubstations}
            onChange={(v: any, meta?: any) => {
              const next = v ?? [];
              const resolved = isolateOnAllSelected(
                selectedFeederSubstations,
                next,
                feederSubstationOptions,
                meta?.action,
              );
              setSelectedFeederSubstations(resolved);
              // Clear feeders so a cleared discom resets downward
              // and backfill can't refill the discom from a feeder.
              if (resolved.length === 0) setSelectedFeederOptions([]);
            }}
          />
          <span className={hierSepClass}>›</span>
          <SearchBox
            sortAlphabetically={true}
            autoWidth
            label="FEEDER"
            options={filteredFeederOptions}
            isMany
            isClearable
            placeholder="Feeder"
            minWidth={140}
            maxWidth={300}
            openSignal={feederOpenSignal}
            selectedOptions={selectedFeederOptions}
            onChange={(v: any, meta?: any) => {
              const next = v ?? [];
              setSelectedFeederOptions(
                isolateOnAllSelected(
                  selectedFeederOptions,
                  next,
                  filteredFeederOptions,
                  meta?.action,
                ),
              );
            }}
          />
        </>
      )}
      {show.substation && (
        <>
          <SearchBox
            sortAlphabetically={true}
            label="CIRCLE"
            options={circleOptions}
            placeholder="Circle"
            minWidth={140}
            isClearable
            selectedOptions={selectedCircle ? [selectedCircle] : []}
            onChange={(v: any) => {
              setSelectedCircle(pickLastSingle(v));
              setSelectedSubstationOptions([]);
            }}
          />
          <span className={hierSepClass}>›</span>
          <SearchBox
            sortAlphabetically={true}
            autoWidth
            isClearable
            label="SUBSTATION"
            options={substationOptions}
            isMany
            placeholder="Substation"
            selectedOptions={selectedSubstationOptions}
            onChange={(v: any, meta?: any) => {
              const next = v ?? [];
              setSelectedSubstationOptions(
                isolateOnAllSelected(
                  selectedSubstationOptions,
                  next,
                  substationOptions,
                  meta?.action,
                ),
              );
            }}
          />
        </>
      )}
      {show.discomSubstation && (
        <>
          <SearchBox
            sortAlphabetically={true}
            label="CIRCLE"
            options={circleOptions}
            placeholder="Circle"
            minWidth={140}
            isClearable
            selectedOptions={selectedCircle ? [selectedCircle] : []}
            onChange={(v: any) => {
              setSelectedCircle(pickLastSingle(v));
              setSelectedTranscoForDiscom([]);
              setSelectedDiscomSubstations([]);
            }}
          />
          <span className={hierSepClass}>›</span>
          <SearchBox
            isMany
            autoWidth
            isClearable
            label="TRANSCO S/S"
            sortAlphabetically={true}
            options={transcoOptionsForDiscom}
            placeholder="Transco Substation"
            selectedOptions={selectedTranscoForDiscom}
            onChange={(v: any, meta?: any) => {
              const next = v ?? [];
              const resolved = isolateOnAllSelected(
                selectedTranscoForDiscom,
                next,
                transcoOptionsForDiscom,
                meta?.action,
              );
              setSelectedTranscoForDiscom(resolved);
              // Clear discom so the discom→transco sync can't
              // immediately refill a cleared transco.
              if (resolved.length === 0) setSelectedDiscomSubstations([]);
            }}
          />
          <span className={hierSepClass}>›</span>
          <SearchBox
            isMany
            autoWidth
            isClearable
            label="DISCOM S/S"
            sortAlphabetically={true}
            options={filteredDiscomSubstationOptions}
            placeholder="Discom Substation"
            selectedOptions={selectedDiscomSubstations}
            onChange={(v: any, meta?: any) => {
              const next = v ?? [];
              setSelectedDiscomSubstations(
                isolateOnAllSelected(
                  selectedDiscomSubstations,
                  next,
                  filteredDiscomSubstationOptions,
                  meta?.action,
                ),
              );
            }}
          />
        </>
      )}
      {show.netConsumption && (
        <div className="flex items-center gap-2">
          {netLevels.circle && (
            <SearchBox
              isMany={true}
              singleSelect
              isClearable
              label="CIRCLE"
              accentColor="#F1935C"
              sortAlphabetically={true}
              options={netCircleOptions}
              placeholder="Circle"
              selectedOptions={netCircle ? [netCircle] : []}
              onChange={(v: any) => {
                const next = pickLastSingle(v);
                setNetCircle(next);
                setNetTransco(null);
                setNetDiscom(null);
              }}
            />
          )}
          {netLevels.circle && netLevels.transco && (
            <span
              style={{
                color: "#9ca3af",
                fontSize: 20,
                lineHeight: 1,
                userSelect: "none",
              }}
            >
              ›
            </span>
          )}
          {netLevels.transco && (
          <SearchBox
            isMany={true}
            singleSelect
            isClearable
            label="TRANSCO S/S"
            accentColor="#F1935C"
            sortAlphabetically={true}
            options={netTranscoOptions}
            placeholder="Transco Substation"
            selectedOptions={netTransco ? [netTransco] : []}
            onChange={(v: any) => {
              const next = pickSingleWithIsolate(
                netTransco,
                v,
                netTranscoOptions,
              );
              setNetTransco(next);
              // Clear discom so the discom→transco backfill can't
              // immediately refill a cleared transco.
              setNetDiscom(null);
            }}
          />
          )}
          {netLevels.transco && netLevels.discom && (
            <span
              style={{
                color: "#9ca3af",
                fontSize: 20,
                lineHeight: 1,
                userSelect: "none",
              }}
            >
              ›
            </span>
          )}
          {netLevels.discom && (
          <SearchBox
            isMany={true}
            singleSelect
            isClearable
            label="DISCOM S/S"
            accentColor="#F1935C"
            sortAlphabetically={true}
            options={netDiscomOptions}
            placeholder="Discom Substation"
            selectedOptions={netDiscom ? [netDiscom] : []}
            onChange={(v: any) => {
              const next = pickSingleWithIsolate(
                netDiscom,
                v,
                netDiscomOptions,
              );
              setNetDiscom(next);
              // backward map: auto-fill transco
              if (next) {
                const discomRaw = discomSubstations.find(
                  (d) => d.discom_substation_id === next.value,
                );
                if (discomRaw?.transco_substation_id) {
                  const t = netTranscoOptions.find(
                    (x) => x.value === discomRaw.transco_substation_id,
                  );
                  if (t) setNetTransco(t);
                }
              }
            }}
          />
          )}
        </div>
      )}
      {show.generation && (
        <>
          <SearchBox
            isMany={true}
            singleSelect
            isClearable
            autoWidth
            label="CIRCLE"
            accentColor="#F1935C"
            sortAlphabetically={true}
            options={genCircleOptions}
            placeholder="Circle"
            minWidth={140}
            selectedOptions={selectedGenCircle ? [selectedGenCircle] : []}
            onChange={(v: any) => {
              const next = pickLastSingle(v);
              setSelectedGenCircle(next);
              // New circle scopes the plant list — drop stale plant-type and
              // plant chips so they can't reference plants from another circle.
              setSelectedPlantTypeOptions([]);
              setSelectedPlantOptions([]);
            }}
          />
          <span className={hierSepClass}>›</span>
          <SearchBox
            sortAlphabetically={true}
            label="PLANT TYPE"
            options={plantTypeOptions}
            isMany={true}
            placeholder="Plant Type"
            selectedOptions={selectedPlantTypeOptions}
            onChange={(val: any[], meta?: any) => {
              const next = val || [];
              setSelectedPlantTypeOptions(
                isolateOnAllSelected(
                  selectedPlantTypeOptions,
                  next,
                  plantTypeOptions,
                  meta?.action,
                ),
              );
            }}
          />
          <SearchBox
            sortAlphabetically={true}
            label="PLANT"
            options={plantOptions}
            isMany={true}
            placeholder="Plant"
            selectedOptions={selectedPlantOptions}
            onChange={(v: any, meta?: any) => {
              const raw: { value: number; label: string }[] = v || [];
              const selected = isolateOnAllSelected(
                selectedPlantOptions,
                raw,
                plantOptions,
                meta?.action,
              );
              setSelectedPlantOptions(selected);
              if (selected.length > 0) {
                const types = Array.from(
                  new Set(
                    selected
                      .map(
                        (o) =>
                          allPlants.find((pl) => pl.id === o.value)?.plant_type,
                      )
                      .filter(Boolean) as JvvnlPlantType[],
                  ),
                ).map((t) => ({ value: t, label: t }));
                setSelectedPlantTypeOptions(types);
                // Backfill the CIRCLE box from the first picked plant when it's
                // empty, mirroring the discom→transco backfill. Only fill when
                // empty — never override a circle the user already chose.
                if (!selectedGenCircle) {
                  const cid = allPlants.find(
                    (pl) => pl.id === selected[0].value,
                  )?.circle_id;
                  if (cid != null) {
                    const c = genCircleOptions.find((o) => o.value === cid);
                    if (c) setSelectedGenCircle(c);
                  }
                }
              }
            }}
          />
        </>
      )}
    </div>
  );
}
